import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Observable, of, timer } from 'rxjs';
import {
  catchError,
  delay,
  distinctUntilChanged,
  filter,
  map,
  retryWhen,
  scan,
  switchMap,
  takeUntil,
  tap,
} from 'rxjs/operators';

import {
  BundleUpdateApi,
  BundleUpdateCheckResponse,
  BundleUpdateErrorModel,
  BundleUpdateProcessTypeEnum,
  BundleUpdateProgressEnum,
  BundleUpdateStatusResponse,
  Xota2UpdateProgress,
} from '@vi-iot/client';

import { Action } from '@ngrx/store';
import { isEqual } from 'lodash';
import { InstallationDetailsStore } from '../installation-details';
import { RemoteConfigStore } from '../remote-config';
import { FirmwareUpdateActions } from './firmware-update.actions';
import { isWithinUpdatedHours } from './firmware-update.helpers';
import { localStorageTargetBundleVersionKey } from './firmware-update.model';
import { FirmwareUpdateStore } from './firmware-update.store';

@Injectable({ providedIn: 'root' })
export class FirmwareUpdateEffects {
  currentBundleVersionKey = 'currentBundleVersion';

  constructor(
    private actions$: Actions,
    private api: BundleUpdateApi,
    private installationDetailsStore: InstallationDetailsStore,
    private firmwareUpdateStore: FirmwareUpdateStore,
    private remoteConfigStore: RemoteConfigStore
  ) {}

  checkUpdateTimeBlock$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(FirmwareUpdateActions.checkUpdateTimeBlock),
      concatLatestFrom(() => this.remoteConfigStore.remoteConfig$),
      switchMap(([_action, remoteConfig]) => {
        if (remoteConfig) {
          const isInUpdateTimeBlock = isWithinUpdatedHours(
            remoteConfig.isFirmwareUpdateAvailableHours,
            remoteConfig.isFirmwareUpdateAvailableWeekdays
          );

          return of(FirmwareUpdateActions.checkUpdateTimeBlockSuccess({ isInUpdateTimeBlock }));
        } else {
          return of(FirmwareUpdateActions.checkUpdateTimeBlockError());
        }
      })
    );
  });

  checkUpdateVerification$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(FirmwareUpdateActions.checkUpdateVerification),
      concatLatestFrom(() => this.installationDetailsStore.gatewaySerial$),
      switchMap(([_action, gatewaySerial]) =>
        this.api.checkUpdateVerification(gatewaySerial).pipe(
          map((response: BundleUpdateCheckResponse) =>
            FirmwareUpdateActions.checkUpdateVerificationSuccess({ response })
          ),
          catchError((error: BundleUpdateErrorModel) => {
            return of(
              FirmwareUpdateActions.checkUpdateVerificationError({
                error,
              })
            );
          })
        )
      )
    );
  });

  checkUpdateFirmwareVerification$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(FirmwareUpdateActions.checkUpdateFirmwareVerification),
      concatLatestFrom(() => this.installationDetailsStore.gatewaySerial$),
      switchMap(([_action, gatewaySerial]) =>
        this.api.checkUpdateFirmwareVerification(gatewaySerial).pipe(
          map((response: BundleUpdateCheckResponse) =>
            FirmwareUpdateActions.checkUpdateFirmwareVerificationSuccess({ response })
          ),
          catchError((error: BundleUpdateErrorModel) => {
            return of(
              FirmwareUpdateActions.checkUpdateFirmwareVerificationError({
                error,
              })
            );
          })
        )
      )
    );
  });

  checkSalesForceCaseNumberForCheckingProcess$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(FirmwareUpdateActions.checkingSalesForceCodeForCheckProcess),
      concatLatestFrom(() => this.installationDetailsStore.gatewaySerial$),
      switchMap(([action, gatewaySerial]) =>
        this.api.checkUpdateVerification(gatewaySerial, action.salesForceCaseNumber).pipe(
          map((response) =>
            FirmwareUpdateActions.checkingSalesForceCodeForCheckProcessSuccess({ response })
          ),
          catchError((error: BundleUpdateErrorModel) => {
            return of(
              FirmwareUpdateActions.checkingSalesForceCodeForCheckProcessError({
                error,
              })
            );
          })
        )
      )
    );
  });

  checkSalesForceCaseNumberForUpdateProcess$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(FirmwareUpdateActions.checkingSalesForceCodeForUpdateProcess),
      concatLatestFrom(() => this.installationDetailsStore.gatewaySerial$),
      switchMap(([action, gatewaySerial]) => {
        return this.api
          .checkUpdateFirmwareVerification(gatewaySerial, action.salesForceCaseNumber)
          .pipe(
            map((response: BundleUpdateCheckResponse) => {
              return FirmwareUpdateActions.checkingSalesForceCodeForUpdateProcessSuccess({
                response,
              });
            }),
            switchMap(() => {
              return this.handleFirmwareProgress(gatewaySerial);
            }),
            catchError((error: BundleUpdateErrorModel) => {
              return of(
                FirmwareUpdateActions.checkingSalesForceCodeForUpdateProcessError({ error })
              );
            }),
            takeUntil(this.actions$.pipe(ofType(FirmwareUpdateActions.stopApiCallsOnProgressEnd)))
          );
      })
    );
  });

  checkOverallProgressProcess$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(
        FirmwareUpdateActions.setOverallProgress,
        FirmwareUpdateActions.setOverallProgressFirmwareUpdate
      ),
      concatLatestFrom(() => [this.installationDetailsStore.gatewaySerial$]),

      switchMap(([action, gatewaySerial]) => {
        if (action.type === FirmwareUpdateActions.setOverallProgress.type) {
          return this.handleCheckProgress(gatewaySerial);
        } else if (action.type === FirmwareUpdateActions.setOverallProgressFirmwareUpdate.type) {
          return this.handleFirmwareProgress(gatewaySerial);
        }
      })
    );
  });

  updatingFirmwareVersion$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(FirmwareUpdateActions.triggerUpdateFirmware),
      concatLatestFrom(() => this.installationDetailsStore.gatewaySerial$),
      switchMap(([_action, gatewaySerial]) =>
        this.api.checkUpdateFirmwareVerification(gatewaySerial).pipe(
          switchMap(() => this.handleFirmwareProgress(gatewaySerial)),
          catchError((error: BundleUpdateErrorModel) =>
            of(FirmwareUpdateActions.checkUpdateVerificationError({ error }))
          ),
          takeUntil(this.actions$.pipe(ofType(FirmwareUpdateActions.stopApiCallsOnProgressEnd)))
        )
      )
    );
  });

  clearLocalStorageKey$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(FirmwareUpdateActions.clearLocalStorageKey),
        tap((action) => {
          const localStorageKey = `${localStorageTargetBundleVersionKey}-${action.gatewaySerial}`;
          localStorage.removeItem(localStorageKey);
        })
      );
    },
    { dispatch: false }
  );

  saveFirmwareUpdateStateToLocal$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(FirmwareUpdateActions.saveFirmwareUpdateStateToLocal),
        concatLatestFrom(() => [
          this.installationDetailsStore.gatewaySerial$,
          this.firmwareUpdateStore.bundleUpdateStep$,
        ]),
        switchMap(([_action, gatewaySerial, bundleUpdateStep]) => {
          return this.api.checkOverallProgress(gatewaySerial).pipe(
            tap((bundleUpdateStatus: BundleUpdateStatusResponse) => {
              const localStorageKey = `${localStorageTargetBundleVersionKey}-${gatewaySerial}`;
              localStorage.setItem(
                localStorageKey,
                JSON.stringify({
                  gatewaySerial: gatewaySerial,
                  processType: bundleUpdateStatus.data.processType,
                  progress: bundleUpdateStatus.data.progress,
                  bundleUpdateStep: bundleUpdateStep,
                })
              );
            })
          );
        })
      );
    },
    { dispatch: false }
  );

  stopApiCallsOnProgressEnd$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(
        FirmwareUpdateActions.showFirmwareBundleUpdate,
        FirmwareUpdateActions.showUpToDate,
        FirmwareUpdateActions.xota1showFirmwareBundleAfterUpdate,
        FirmwareUpdateActions.xota2showProcessStatusAfterUpdate
      ),
      filter((action) => action.bundleUpdateStatusResponse.data.overallProgress === 100),
      map(() => FirmwareUpdateActions.stopApiCallsOnProgressEnd())
    );
  });

  closeFirmwareUpdateModal$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(FirmwareUpdateActions.onCloseFirmwareUpdateModal),
      map(() => FirmwareUpdateActions.stopApiCallsOnProgressEnd())
    );
  });

  verifyGateway$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(FirmwareUpdateActions.verifyGateway.attempt),
      concatLatestFrom(() => [this.installationDetailsStore.gatewaySerial$]),
      switchMap(([_action, gatewaySerial]) =>
        this.api.verifyGateway(gatewaySerial).pipe(
          map((response) =>
            FirmwareUpdateActions.verifyGateway.success({ value: response.data.isAllowed })
          ),

          catchError((error) => of(FirmwareUpdateActions.verifyGateway.errror({ error })))
        )
      )
    );
  });

  private handleXota1Progress(
    bundleUpdateStatusResponse: BundleUpdateStatusResponse,
    gatewaySerial: string
  ): Action {
    const {
      progress,
      currentBundleVersion,
      missingEcusInTopology,
      tcuStatus,
    } = bundleUpdateStatusResponse.data;

    if (tcuStatus === Xota2UpdateProgress.FAILED) {
      return FirmwareUpdateActions.xota1showFirmwareBundleAfterUpdate({
        bundleUpdateStatusResponse,
        gatewaySerial,
      });
    } else {
      switch (progress) {
        case BundleUpdateProgressEnum.Finished:
          const missingEcusArray = missingEcusInTopology ? missingEcusInTopology.split(',') : [];

          const modifiedCurrentBundleVersion = [...currentBundleVersion];

          missingEcusArray.forEach((ecuName) => {
            modifiedCurrentBundleVersion.push({
              ecuName,
              otaVersions: [],
              isMissing: true,
            });
          });

          const localCurrentVersionBundleKey = `${this.currentBundleVersionKey}`;
          localStorage.setItem(
            localCurrentVersionBundleKey,
            JSON.stringify(modifiedCurrentBundleVersion)
          );

          localStorage.removeItem(localCurrentVersionBundleKey);

          return FirmwareUpdateActions.xota1showFirmwareBundleAfterUpdate({
            bundleUpdateStatusResponse,
            gatewaySerial,
          });
        case BundleUpdateProgressEnum.Ongoing:
        case BundleUpdateProgressEnum.Scheduled:
          return FirmwareUpdateActions.setOverallProgressFirmwareUpdateSuccess({
            bundleUpdateStatusResponse,
            gatewaySerial,
          });
      }
    }
  }

  private handleXota2Progress(
    bundleUpdateStatusResponse: BundleUpdateStatusResponse,
    gatewaySerial: string
  ): Action {
    const {
      progress,
      tcuStatus,
      updateMethod,
      containerStatus,
      transferStatus,
      updateStatus,
      reportStatus,
    } = bundleUpdateStatusResponse.data;

    if (tcuStatus === Xota2UpdateProgress.NULL && updateMethod === Xota2UpdateProgress.XOTA_ONE) {
      return FirmwareUpdateActions.showUpToDate({
        bundleUpdateStatusResponse,
      });
    }

    const isOperationProcessFailed = [
      tcuStatus,
      containerStatus,
      transferStatus,
      updateStatus,
      reportStatus,
    ].some((status) => {
      return status === Xota2UpdateProgress.FAILED;
    });

    if (isOperationProcessFailed) {
      return FirmwareUpdateActions.xota2showProcessStatusAfterUpdate({
        bundleUpdateStatusResponse,
        gatewaySerial,
      });
    } else {
      switch (progress) {
        case BundleUpdateProgressEnum.Finished:
          return FirmwareUpdateActions.xota2showProcessStatusAfterUpdate({
            bundleUpdateStatusResponse,
            gatewaySerial,
          });
        case BundleUpdateProgressEnum.Ongoing:
        case BundleUpdateProgressEnum.Scheduled:
          return FirmwareUpdateActions.setOverallProgressFirmwareUpdateSuccess({
            bundleUpdateStatusResponse,
            gatewaySerial,
          });
      }
    }
  }

  private handleFirmwareProgress(gatewaySerial: string): Observable<Action> {
    return timer(0, 7000).pipe(
      switchMap(() =>
        this.api.checkOverallProgress(gatewaySerial).pipe(
          distinctUntilChanged(isEqual),
          filter(
            (bundleUpdateStatus: BundleUpdateStatusResponse) =>
              bundleUpdateStatus.data.processType === BundleUpdateProcessTypeEnum.BundleUpdate
          ),
          map((bundleUpdateStatusResponse: BundleUpdateStatusResponse) => {
            const { progress, updateMethod } = bundleUpdateStatusResponse.data;
            let action: Action;

            if (!updateMethod) {
              if (
                progress === BundleUpdateProgressEnum.Ongoing ||
                progress === BundleUpdateProgressEnum.Scheduled
              ) {
                action = FirmwareUpdateActions.setOverallProgressFirmwareUpdateSuccess({
                  bundleUpdateStatusResponse,
                  gatewaySerial,
                });
              }
            } else {
              if (updateMethod === Xota2UpdateProgress.XOTA_ONE) {
                action = this.handleXota1Progress(bundleUpdateStatusResponse, gatewaySerial);
              } else if (updateMethod === Xota2UpdateProgress.XOTA_TWO) {
                action = this.handleXota2Progress(bundleUpdateStatusResponse, gatewaySerial);
              }
            }

            return action;
          }),
          retryWhen((errors) =>
            errors.pipe(
              scan((retryIndex) => {
                if (retryIndex === 5) {
                  throw new Error('firmware check/update failed');
                }

                return retryIndex + 1;
              }, 1),
              delay(2000)
            )
          ),
          catchError((error) => {
            const formattedError = error instanceof Error ? { message: error.message } : error;

            return of(
              FirmwareUpdateActions.setOverallProgressFirmwareUpdateError({ error: formattedError })
            );
          }),
          takeUntil(this.actions$.pipe(ofType(FirmwareUpdateActions.stopApiCallsOnProgressEnd)))
        )
      ),
      takeUntil(this.actions$.pipe(ofType(FirmwareUpdateActions.stopApiCallsOnProgressEnd)))
    );
  }

  private handleCheckProgress(gatewaySerial: string): Observable<Action> {
    return timer(0, 15000).pipe(
      switchMap(() =>
        this.api.checkOverallProgress(gatewaySerial).pipe(
          distinctUntilChanged(isEqual),
          filter(
            (bundleUpdateStatus: BundleUpdateStatusResponse) =>
              bundleUpdateStatus.data.processType ===
              BundleUpdateProcessTypeEnum.CheckForBundleUpdate
          ),
          map((bundleUpdateStatusResponse: BundleUpdateStatusResponse) => {
            const {
              progress,
              updateIsAvailable,
              targetBundleVersion,
              missingEcusInTopology,
            } = bundleUpdateStatusResponse.data;
            switch (progress) {
              case BundleUpdateProgressEnum.Finished:
                if (updateIsAvailable) {
                  const missingEcusArray = missingEcusInTopology
                    ? missingEcusInTopology.split(',')
                    : [];
                  const modifiedTargetBundleVersion = [...targetBundleVersion];
                  missingEcusArray.forEach((ecuName) => {
                    modifiedTargetBundleVersion.push({
                      ecuName,
                      otaVersions: [],
                      isMissing: true,
                    });
                  });
                  const localCurrentVersionBundleKey = `${this.currentBundleVersionKey}-${gatewaySerial}`;
                  localStorage.setItem(
                    localCurrentVersionBundleKey,
                    JSON.stringify(modifiedTargetBundleVersion)
                  );

                  return FirmwareUpdateActions.showFirmwareBundleUpdate({
                    bundleUpdateStatusResponse,
                  });
                }

                return FirmwareUpdateActions.showUpToDate({
                  bundleUpdateStatusResponse,
                });
              case BundleUpdateProgressEnum.Ongoing:
              case BundleUpdateProgressEnum.Scheduled:
                return FirmwareUpdateActions.setOverallProgressSuccess({
                  bundleUpdateStatusResponse,
                  gatewaySerial,
                });
            }
          }),
          retryWhen((errors) =>
            errors.pipe(
              scan((retryIndex) => {
                if (retryIndex === 5) {
                  throw new Error('firmware check/update failed');
                }

                return retryIndex + 1;
              }, 1),
              delay(2000)
            )
          ),
          catchError((error) => {
            const formattedError = error instanceof Error ? { message: error.message } : error;

            return of(FirmwareUpdateActions.setOverallProgressError({ error: formattedError }));
          }),
          takeUntil(this.actions$.pipe(ofType(FirmwareUpdateActions.stopApiCallsOnProgressEnd)))
        )
      ),
      takeUntil(this.actions$.pipe(ofType(FirmwareUpdateActions.stopApiCallsOnProgressEnd)))
    );
  }
}
